/**
 * Lightweight GA4 + GTM event helper.
 *
 * Pushes to `window.dataLayer` (consumed by GTM) AND calls `gtag` directly,
 * so events flow whether GA4 is wired through GTM or via a standalone gtag.js
 * snippet. Safe to call before either script loads — events queue on the
 * dataLayer until GTM/GA4 boots.
 */

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
    gtag?: (...args: unknown[]) => void;
  }
}

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

// ---------------------------------------------------------------------------
// Attribution capture (UTM + landing page) — first-touch per session
// ---------------------------------------------------------------------------
export interface Attribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  msclkid?: string;
  landing_page?: string;
  landing_page_path?: string;
  landing_referrer?: string;
  landing_referrer_host?: string;
  landed_at?: string;
  traffic_source_inferred?: string; // direct | referral | organic_search | paid | social | email | internal
}

const ATTRIBUTION_KEY = "mcr_attribution";
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const CLICK_ID_KEYS = ["gclid", "fbclid", "ttclid", "msclkid"] as const;

const SEARCH_HOSTS = /(google|bing|yahoo|duckduckgo|ecosia|brave|baidu|yandex)\./i;
const SOCIAL_HOSTS = /(facebook|instagram|t\.co|twitter|linkedin|tiktok|reddit|pinterest|youtube)\./i;

function inferTrafficSource(utmMedium: string | undefined, referrer: string): string {
  if (utmMedium) {
    if (/^(cpc|ppc|paid|cpm|paidsearch|paid_search)$/i.test(utmMedium)) return "paid";
    if (/^(social|paid_social|paidsocial)$/i.test(utmMedium)) return "social";
    if (/^(email|newsletter)$/i.test(utmMedium)) return "email";
    if (/^(referral)$/i.test(utmMedium)) return "referral";
    if (/^(organic)$/i.test(utmMedium)) return "organic_search";
  }
  if (!referrer) return "direct";
  try {
    const host = new URL(referrer).hostname;
    if (SEARCH_HOSTS.test(host)) return "organic_search";
    if (SOCIAL_HOSTS.test(host)) return "social";
    if (host === window.location.hostname) return "internal";
    return "referral";
  } catch {
    return "direct";
  }
}

function captureAttribution(): Attribution | null {
  if (typeof window === "undefined") return null;
  try {
    // First-touch wins for the session — keeps lead attribution stable even
    // if the user clicks around and loses the original UTM in the URL.
    const existingRaw = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (existingRaw) return JSON.parse(existingRaw) as Attribution;

    const url = new URL(window.location.href);
    const params = url.searchParams;
    const attribution: Attribution = {};

    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) attribution[k] = v.slice(0, 100);
    }
    for (const k of CLICK_ID_KEYS) {
      const v = params.get(k);
      if (v) attribution[k] = v.slice(0, 200);
    }

    // If a click came in via gclid/fbclid without explicit utm_medium, GA4
    // still attributes paid — mirror that hint here so our event params are
    // accurate even when the destination URL was not fully tagged.
    if (!attribution.utm_medium) {
      if (attribution.gclid) attribution.utm_medium = "cpc";
      else if (attribution.fbclid || attribution.ttclid) attribution.utm_medium = "paid_social";
    }

    attribution.landing_page = (window.location.origin + url.pathname + url.search).slice(0, 500);
    attribution.landing_page_path = url.pathname;
    attribution.landing_referrer = (document.referrer || "").slice(0, 500) || undefined;
    if (attribution.landing_referrer) {
      try {
        attribution.landing_referrer_host = new URL(attribution.landing_referrer).hostname;
      } catch {
        /* ignore */
      }
    }
    attribution.landed_at = new Date().toISOString();
    attribution.traffic_source_inferred = inferTrafficSource(
      attribution.utm_medium,
      attribution.landing_referrer || "",
    );

    sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(attribution));
    return attribution;
  } catch {
    return null;
  }
}

let cachedAttribution: Attribution | null = null;
export function getAttribution(): Attribution {
  if (!cachedAttribution) cachedAttribution = captureAttribution();
  return cachedAttribution || {};
}

// Eagerly capture on module load so referrer/UTM aren't lost if the user
// navigates client-side before the first event fires.
if (typeof window !== "undefined") {
  cachedAttribution = captureAttribution();
}


// ---- In-memory ring buffer for the AnalyticsDebugPanel --------------------
export interface DebugAnalyticsEvent {
  id: number;
  name: string;
  params: AnalyticsParams;
  timestamp: number;
  gtagSent: boolean;
}

const DEBUG_BUFFER_LIMIT = 50;
const debugBuffer: DebugAnalyticsEvent[] = [];
let debugCounter = 0;

export function getRecentAnalyticsEvents(): DebugAnalyticsEvent[] {
  return debugBuffer.slice();
}

export function clearRecentAnalyticsEvents() {
  debugBuffer.length = 0;
  window.dispatchEvent(new CustomEvent("analytics:debug-cleared"));
}

export function trackEvent(eventName: string, params: AnalyticsParams = {}) {
  try {
    // Strip undefined values for cleaner payloads
    const clean: AnalyticsParams = {};
    for (const [k, v] of Object.entries(params)) {
      if (v !== undefined) clean[k] = v;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: eventName, ...clean });

    const gtagSent = typeof window.gtag === "function";
    if (gtagSent) {
      window.gtag!("event", eventName, clean);
    }

    // Push to debug buffer + notify panel
    const entry: DebugAnalyticsEvent = {
      id: ++debugCounter,
      name: eventName,
      params: clean,
      timestamp: Date.now(),
      gtagSent,
    };
    debugBuffer.unshift(entry);
    if (debugBuffer.length > DEBUG_BUFFER_LIMIT) debugBuffer.length = DEBUG_BUFFER_LIMIT;
    window.dispatchEvent(new CustomEvent<DebugAnalyticsEvent>("analytics:event", { detail: entry }));
  } catch (err) {
    // Never let analytics break the UI
    console.warn("[analytics] trackEvent failed", err);
  }
}

/**
 * Fire a standardized lead/contact form submission event.
 * Pushes BOTH a custom `lead_form_submit` (easy to filter in GA4 Explore)
 * AND the GA4-recommended `generate_lead` event (used by GA4 conversions
 * and Google Ads import). Attribution (UTM, click IDs, landing page,
 * referrer) is auto-merged so every lead carries its source context.
 */
export function trackLeadSubmit(
  formName: string,
  params: AnalyticsParams = {},
) {
  const payload = {
    form_name: formName,
    ...getAttribution(),
    ...params,
  };
  trackEvent("lead_form_submit", payload);
  trackEvent("generate_lead", payload);
}

/**
 * Wrapper for rideshare funnel events that auto-attaches attribution +
 * landing page context. Use this for every step/error/abandon event so the
 * full funnel is sliceable by source/medium/campaign in GA4 Explore.
 */
export function trackRideshareEvent(eventName: string, params: AnalyticsParams = {}) {
  trackEvent(eventName, { ...getAttribution(), ...params });
}

/**
 * Light keyword matcher used to enrich free-text lead submissions with
 * `vehicle_interest` and `location_interest` params — no PII is sent.
 */
const VEHICLE_KEYWORDS: Array<{ key: string; pattern: RegExp }> = [
  { key: "model_3", pattern: /\bmodel\s*3\b/i },
  { key: "model_y", pattern: /\bmodel\s*y\b/i },
  { key: "model_s", pattern: /\bmodel\s*s\b/i },
  { key: "model_x", pattern: /\bmodel\s*x\b/i },
  { key: "cybertruck", pattern: /\bcyber\s*truck\b/i },
  { key: "tesla_generic", pattern: /\btesla\b/i },
];

const LOCATION_KEYWORDS: Array<{ key: string; pattern: RegExp }> = [
  { key: "bayonne", pattern: /\bbayonne\b/i },
  { key: "jersey_city", pattern: /\bjersey\s*city\b/i },
  { key: "hoboken", pattern: /\bhoboken\b/i },
  { key: "newark", pattern: /\bnewark\b/i },
  { key: "ewr", pattern: /\b(ewr|newark\s*airport)\b/i },
  { key: "jfk", pattern: /\bjfk\b/i },
  { key: "lga", pattern: /\b(lga|laguardia)\b/i },
  { key: "manhattan", pattern: /\b(manhattan|nyc|new\s*york)\b/i },
  { key: "brooklyn", pattern: /\bbrooklyn\b/i },
];

export function extractLeadInterests(text: string | undefined | null) {
  const t = text || "";
  const vehicles = VEHICLE_KEYWORDS.filter((k) => k.pattern.test(t)).map((k) => k.key);
  const locations = LOCATION_KEYWORDS.filter((k) => k.pattern.test(t)).map((k) => k.key);
  return {
    vehicle_interest: vehicles.join(",") || undefined,
    location_interest: locations.join(",") || undefined,
  };
}
